import { isCommodity } from '@enmon/commons';
import type { CountableApiResponse, SerializedCombinedMeter } from '@enmon/commons/lib/enmon';
import { readLocationBootstrapMeter } from '@/features/location-bootstrap/location-bootstrap-meter-mapper';
import {
  readLocationBootstrapData,
  readMeterModels,
} from '@/features/location-bootstrap/location-bootstrap-response-mappers';
import type {
  LocationBootstrapLocation,
  LocationBootstrapMeter,
  LocationBootstrapThirdParty,
} from '@/features/location-bootstrap/location-bootstrap-types';
import type { InventoryAxiosClient } from '@/shared/api/default-axios-client';
import { uniqueStrings } from '@/shared/unique-strings';

const LOCATION_PARAMS = {
  'populate[0]': 'inventory_location_buildings',
} as const;

const METER_MODEL_PARAMS = {
  'fields[0]': 'id',
  'fields[1]': 'name',
  'fields[2]': 'commodity',
  'fields[3]': 'details',
  'pagination[limit]': -1,
  'pagination[start]': 1,
  'populate[manufacturer][fields][0]': 'id',
  'populate[manufacturer][fields][1]': 'name',
  'populate[meter_addons][fields][0]': 'id',
  'populate[meter_addons][fields][1]': 'name',
} as const;

export function loadLocationData(input: { axios: InventoryAxiosClient; locationId: number | string }) {
  return input.axios
    .get<{ data: LocationBootstrapLocation }>(`/inventory-locations/${input.locationId}`, {
      params: LOCATION_PARAMS,
    })
    .then(response => readLocationBootstrapData(response.data.data));
}

export function loadMeterModels(axios: InventoryAxiosClient) {
  return axios
    .get<{ data: unknown[] }>('/meter-models', {
      params: METER_MODEL_PARAMS,
    })
    .then(response => readMeterModels(response.data));
}

export function loadTenantValues(axios: InventoryAxiosClient) {
  return axios
    .get<(string | null)[]>('/inventory-meters/distinct', {
      params: {
        field: 'tenant',
      },
    })
    .then(response => uniqueStrings(response.data));
}

export function loadOwnerValues(axios: InventoryAxiosClient) {
  return axios
    .get<(string | null)[]>('/inventory-meters/distinct', {
      params: {
        field: 'owner',
      },
    })
    .then(response => uniqueStrings(response.data));
}

export function loadThirdParties(axios: InventoryAxiosClient) {
  return axios
    .get<{ data: Array<{ name?: string; commodities?: unknown }> }>('/third-parties', {
      params: {
        'fields[0]': 'name',
        'fields[1]': 'commodities',
        'pagination[limit]': -1,
      },
    })
    .then(response => response.data.data.flatMap(readThirdParty));
}

export async function loadLocationMeters(input: { axios: InventoryAxiosClient; locationId: number | string }) {
  return (await loadRawLocationMeters(input))
    .map(readLocationBootstrapMeter)
    .filter((meter): meter is LocationBootstrapMeter => Boolean(meter));
}

const LOCATION_METERS_PAGE_SIZE = 500;

export async function loadRawLocationMeters(input: {
  axios: InventoryAxiosClient;
  locationId: number | string;
  requestTimeoutMs?: number;
}) {
  // A listing paged while meters are added or removed can skip entries; one fresh pass resolves that.
  for (let attempt = 0; ; attempt += 1) {
    const { count, meters } = await loadLocationMeterPages(input);
    if (meters.length >= count) return meters;
    if (attempt > 0) throw new Error('Incomplete location meter listing.');
  }
}

async function loadLocationMeterPages(input: {
  axios: InventoryAxiosClient;
  locationId: number | string;
  requestTimeoutMs?: number;
}) {
  const metersById = new Map<string, SerializedCombinedMeter>();
  let count = 0;

  for (let page = 1; ; page += 1) {
    const { data } = await input.axios.get<CountableApiResponse<SerializedCombinedMeter[]>>(
      `/inventory-locations/${input.locationId}/meters`,
      { params: { page, pageSize: LOCATION_METERS_PAGE_SIZE }, timeout: input.requestTimeoutMs },
    );
    if (!(Array.isArray(data.result) && Number.isFinite(data.count))) {
      throw new Error('Invalid location meter listing.');
    }

    const sizeBefore = metersById.size;
    for (const meter of data.result) metersById.set(meter._id, meter);
    count = data.count;
    const addedNewMeters = metersById.size > sizeBefore;
    if (!addedNewMeters || metersById.size >= count || data.result.length < LOCATION_METERS_PAGE_SIZE) {
      return { count, meters: [...metersById.values()] };
    }
  }
}

function readThirdParty(record: { name?: string; commodities?: unknown }): LocationBootstrapThirdParty[] {
  const name = record.name;

  if (!name) {
    return [];
  }

  return [
    {
      name,
      commodities: readCommodities(record?.commodities),
    },
  ];
}

function readCommodities(value: unknown) {
  return typeof value === 'string'
    ? value
        .split(',')
        .map(value => value.trim())
        .filter(isCommodity)
    : [];
}
